import React, { useState } from 'react';
import { motion } from 'framer-motion';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * 实时反馈条
 * 每条命理解读后显示，支持快速反馈和展开详细说明
 */
function FeedbackBar({ messageContent, contextType, userId, sessionId, onFeedbackSubmit }) {
  const [feedback, setFeedback] = useState(null); // null=未反馈, 'accurate'|'partial'|'inaccurate'|'skipped'
  const [showDetail, setShowDetail] = useState(false);
  const [detailText, setDetailText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleQuickFeedback = async (type) => {
    if (submitted) return;
    setFeedback(type);

    if (type === 'skipped') {
      setSubmitted(true);
      return;
    }

    if (type === 'partial' || type === 'inaccurate') {
      setShowDetail(true);
      return;
    }

    // 直接提交 "很准"
    await submitFeedback(type);
  };

  const submitFeedback = async (type, content = '') => {
    setSubmitting(true);
    try {
      await fetch(`${API_BASE}/api/zhi/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || null,
          session_id: sessionId || null,
          feedback_type: 'realtime',
          context_message: messageContent?.substring(0, 200) || '',
          context_type: contextType || '',
          accuracy_score: type === 'accurate' ? 1.0 : type === 'partial' ? 0.5 : 0,
          content: content || null,
        }),
      });
    } catch (e) {
      console.warn('反馈提交失败（网络问题）:', e.message);
    }
    setSubmitting(false);
    setSubmitted(true);
    if (onFeedbackSubmit) onFeedbackSubmit(type, content);
  };

  const handleDetailSubmit = () => {
    if (!detailText.trim()) return;
    submitFeedback(feedback, detailText.trim());
    setShowDetail(false);
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-gray-400">感谢你的反馈 🙏</span>
      </div>
    );
  }

  return (
    <div className="mt-1">
      {!feedback ? (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">这个解读</span>
          <button
            onClick={() => handleQuickFeedback('accurate')}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 hover:bg-green-50 text-xs text-gray-500 hover:text-green-700 border border-gray-200 hover:border-green-300 transition-all"
          >
            <span className="text-sm">👍</span> 很准
          </button>
          <button
            onClick={() => handleQuickFeedback('partial')}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 hover:bg-yellow-50 text-xs text-gray-500 hover:text-yellow-700 border border-gray-200 hover:border-yellow-300 transition-all"
          >
            <span className="text-sm">🤔</span> 部分对
          </button>
          <button
            onClick={() => handleQuickFeedback('inaccurate')}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 hover:bg-red-50 text-xs text-gray-500 hover:text-red-700 border border-gray-200 hover:border-red-300 transition-all"
          >
            <span className="text-sm">👎</span> 不准
          </button>
          <button
            onClick={() => handleQuickFeedback('skipped')}
            className="text-[10px] text-gray-400 hover:text-gray-600 ml-1"
          >
            跳过
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">提交反馈中...</span>
        </div>
      )}

      {/* 详细说明弹窗 */}
      {showDetail && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-2"
        >
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-2">
              方便说说是哪部分{feedback === 'partial' ? '不太对' : '不太准'}吗？这会帮助我们改进 🙏
            </p>
            <textarea
              value={detailText}
              onChange={(e) => setDetailText(e.target.value)}
              placeholder="可以简单描述一下..."
              rows={2}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm resize-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200 focus:outline-none"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleDetailSubmit}
                disabled={!detailText.trim() || submitting}
                className="px-4 py-1.5 bg-zhusha text-white rounded-full text-xs font-medium disabled:opacity-50"
              >
                {submitting ? '提交中...' : '提交'}
              </button>
              <button
                onClick={() => {
                  setShowDetail(false);
                  submitFeedback(feedback);
                }}
                className="px-4 py-1.5 bg-gray-200 text-gray-600 rounded-full text-xs"
              >
                跳过说明
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default FeedbackBar;
